import { DatePipe, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, effect, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FilterMatchMode, MessageService, SelectItem, SortEvent, TableState } from 'primeng/api';
import { Checkbox } from 'primeng/checkbox';
import { Fluid } from 'primeng/fluid';
import { InputNumber } from 'primeng/inputnumber';
import { InputText } from 'primeng/inputtext';
import { ScrollPanel } from 'primeng/scrollpanel';
import { Select } from 'primeng/select';
import { Table, TableFilterEvent, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { Observable, Subscription } from 'rxjs';
import { BaseComponent } from '../../../content/components/base/base.component';
import { AmountCurrencyDisplayPipe } from '../../../content/pipes/amount-currency-display.pipe';
import { BooleanToYesNoPipe } from '../../../content/pipes/boolean-to-yes-no.pipe';
import { CustomerStatusTagPipe } from '../../../content/pipes/customer-status-tag.pipe';
import { OrganisationStatusTagPipe } from '../../../content/pipes/organisation-status-tag.pipe';
import { ColumnFilterType } from '../../../core/enums/column-filter-type';
import { ColumnFormat } from '../../../core/enums/column-format';
import { ColumnHeaderType } from '../../../core/enums/column-header-type';
import { ServiceResult } from '../../../core/interfaces/service-result';
import { ArrayLengthPipe } from '../../../core/pipes/array-length.pipe';
import { MultiselectKeyArrayToStringPipe } from '../../../core/pipes/multiselect-key-arrray-to-string.pipe';
import { MultiselectKeysToValuePipe } from '../../../core/pipes/multiselect-keys-to-value.pipe';
import { SafeHtmlPipe } from '../../../core/pipes/safe-html.pipe';
import { SelectKeyToValuePipe } from '../../../core/pipes/select-key-to-value.pipe';
import { StripHtmlTagsPipe } from '../../../core/pipes/strip-html-tags.pipe';
import { HelperService } from '../../../core/services/helper.service';
import { I18nService } from '../../../core/services/i18n.service';
import { LayoutService } from '../../../layout/services/layout.service';
import { ActionData } from '../../interfaces/sps-table/action-data';
import { TableColumn } from '../../interfaces/sps-table/table-column';
import { TableConfig } from '../../interfaces/sps-table/table-config';
import { TableListProvider } from '../../provider/table-list-provider';
import { TableControlService } from '../../services/table-control.service';
import { SpsActivationStatusTagComponent } from '../sps-activation-status-tag/sps-activation-status-tag.component';
import { SpsCustomIconComponent } from '../sps-custom-icon/sps-custom-icon.component';
import { SpsPaymentStatusTagComponent } from '../sps-payment-status-tag/sps-payment-status-tag.component';

@Component({
  selector: 'sps-table',
  imports: [
    TableModule,
    Tooltip,
    NgStyle,
    NgClass,
    Fluid,
    Select,
    FormsModule,
    NgTemplateOutlet,
    DatePipe,
    SelectKeyToValuePipe,
    ScrollPanel,
    MultiselectKeysToValuePipe,
    MultiselectKeyArrayToStringPipe,
    ArrayLengthPipe,
    Checkbox,
    StripHtmlTagsPipe,
    SafeHtmlPipe,
    SpsCustomIconComponent,
    InputNumber,
    Tag,
    AmountCurrencyDisplayPipe,
    OrganisationStatusTagPipe,
    CustomerStatusTagPipe,
    SpsPaymentStatusTagComponent,
    BooleanToYesNoPipe,
    InputText,
    SpsActivationStatusTagComponent
  ],
  templateUrl: './sps-table.component.html'
})
export class SpsTableComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {

  /**
   * enum references
   * @protected
   */
  protected readonly ColumnHeaderType = ColumnHeaderType;
  protected readonly ColumnFilterType = ColumnFilterType;
  protected readonly ColumnFormat = ColumnFormat;

  /**
   * Table input parameters
   */
  @Input() tableConfig: TableConfig;
  @Input() model: any[] = [];
  @Input() freezeColumns: boolean = false;
  @Input() tableListProvider: TableListProvider;
  @Input() minScrollHeight: number;
  @Input() scrollHeightOffset: number;
  @Input() rows: number = null;

  /**
   * LazyLoading Data Provider
   */
  @Input() parentDataProvider: (lazyLoadEvent: TableLazyLoadEvent) => Observable<any> = null;
  /**
   * LazyLoading Data-Mapper
   */
  @Input() parentDataMapper: (parentServiceResponse: any) => ServiceResult = null;
  /**
   * Table-Control from parent
   */
  @Input() tableControlService: TableControlService;
  /**
   * fix row height e.g. if table contains HTML-Scrollpanel- or TextArea-columns
   */
  @Input() fixRowHeight: number = null;
  /**
   * array with names of the columns for activation of fixRowHeight.
   * fixRowHeight will only be activated if one of these columns is visible
   */
  @Input() fixRowHeightActivationColumns: string[] = null;
  /**
   * Show Toggler-Icon (chevron-right) as last column of the table
   */
  @Input() showTogglerIcon: boolean = false;
  /**
   * selectedRow or selectedRows
   */
  @Input() selectedRowsData: any | any[];

  /**
   * table events
   */
  @Output() tableInitialized = new EventEmitter<SpsTableComponent>();
  @Output() dataFetched = new EventEmitter<ServiceResult>();
  @Output() checkboxChecked = new EventEmitter<ActionData>();
  @Output() selectionChanged = new EventEmitter();
  @Output() rowsCountChanged = new EventEmitter<number>();
  @Output() stateRestored = new EventEmitter<TableState>();
  @Output() totalRecordsChanged = new EventEmitter<number>();

  /**
   * Table reference
   */
  @ViewChild('dataTable') dataTable: Table;

  /**
   * Table flags
   */
  initialized: boolean = false;
  dataLoading: boolean = false;
  useFixRowHeight: boolean;
  paginator: boolean = undefined;
  /**
   * Table properties
   */
  first: number = 0;                      // number of the first visible row (paging)
  totalRecords: number = null;            // number of total records
  visibleColumns: TableColumn[] = [];     // array with the visible rows, rendered in the table
  startsWithMatchMode: SelectItem[] = [];    // spezifische Matchmodes für den Amount
  lastSortEvent: SortEvent = null;
  lastLazyLoadEvent: TableLazyLoadEvent = null;

  /**
   * neede for autocomplete fix
   * @private
   */
  private observer: MutationObserver;

  constructor(private http: HttpClient,
              private el: ElementRef,
              public helperService: HelperService,
              public layoutService: LayoutService,
              public i18n: I18nService,
              private messageService: MessageService) {
    super();
    effect(() => {
      if (this.tableControlService.unselectTableRows()) {
        this.selectedRowsData = null;
        this.tableControlService.unselectTableRows.set(false);
      }
      if (this.tableControlService.clearTableFilters() === true) {
        this.selectedRowsData = null;
        /**
         * Lazy-Loading clear differs from Eager clear
         */
        if (this.tableConfig.lazyLoading) {
          try {
            this.dataTable.clearFilterValues();
          } catch (e) {
          }
          this.getTablePageData(
            {
              first: 0,
              rows: this.rows,
              sortField: this?.lastSortEvent ? this.lastSortEvent.field : this.tableConfig.sortField,
              sortOrder: this?.lastSortEvent ? this.lastSortEvent.order : this.tableConfig.sortOrder,
              filters: {}
            }
          );

        } else {
          try {
            this.dataTable.clear();
          } catch (e) {
          }
        }
        setTimeout(() => {
          this.tableControlService.clearTableFilters.set(false);
        }, 100);
      }
      if (this.tableControlService.clearState()) {
        try {
          this.dataTable.clearState();
        } catch (e) {
        }
        this.tableControlService.clearState.set(false);
      }
      if (this.tableControlService.refreshTableData() && this.lastLazyLoadEvent !== null) {
        this.getTablePageData(this.lastLazyLoadEvent);
        this.tableControlService.refreshTableData.set(false);
      }
    });
  }

  ngOnInit() {
    /**
     * match mode option param for columns with only startsWith matchMode
     */
    this.startsWithMatchMode = [
      {
        label: this.i18n.i18nLng()['STARTSWITH'],
        value: FilterMatchMode.STARTS_WITH
      }
    ];
  }

  ngAfterViewInit() {
    /**
     * prepare table columns before table rendering
     */
    setTimeout(() => {
      if (this.tableConfig?.columns) {
        this.prepareTableColumns();
      }
      if (!this.tableConfig?.lazyLoading) {
        this.totalRecords = this.model?.length;
      }
      this.paginator = this.tableConfig?.paginator;
      /**
       * disable autocomplete for all input filters
       * observe when dom is completed, then call applyFix
       */
      this.observer = new MutationObserver(() => this.applyFix());
      this.observer.observe(this.el.nativeElement, { childList: true, subtree: true });
      this.applyFix();
      /**
       * initialization finished
       */
      this.initialized = true;
      this.tableInitialized.emit(this);
    }, 250);
  }

  /**
   * fix to disable autocomplete for all input filters, after dom rendered
   * set autocomplete to 'nope' because chrome ignores 'off'
   * @private
   */
  private applyFix() {
    const inputs = document.querySelectorAll(
      'p-columnfilter input.p-inputtext, .p-columnfilter-overlay input.p-inputtext'
    );
    let filterIndex: number = 0;
    inputs.forEach(input => {
      filterIndex++;
      input.setAttribute('autocomplete', 'nope');
      input.setAttribute('name', 'filter-' + filterIndex);
    });
  }

  /**
   * destroy dom observer for input filters
   */
  override ngOnDestroy() {
    this.observer?.disconnect();
    super.ngOnDestroy();
  }

  /**
   * prepare all specific column properties, like selectLists, fixRowHeight-Property etc.
   * @private
   */
  private prepareTableColumns() {

    // TODO see MegaTable
    // TODO fixed column sorting etc.

    /**
     * Fetch select-lists from tableListProvider
     */
    this.tableConfig?.columns.map(col => {
      if (col?.selectListKey && col.selectListKey.trim().length !== 0) {
        col.selectList = this.tableListProvider.getListByKey(col?.selectListKey);
      }
    });

    /**
     * sort columns by columnOrder
     * and filter hidden columns
     */
    this.visibleColumns = this.tableConfig?.columns.sort((a, b) => a.columnOrder < b.columnOrder ? -1 : 1);
    this.visibleColumns = this.visibleColumns.filter(column => !column.hidden);

    /**
     * check for fixedRowHeight-activation
     */
    this.useFixRowHeight = false;
    if (this.fixRowHeight !== null) {
      if (this.fixRowHeightActivationColumns !== null && this.fixRowHeightActivationColumns.length > 0) {
        this.fixRowHeightActivationColumns.map(fixHeightCol => {
          try {
            if (!this.visibleColumns.find(col => col.columnName === fixHeightCol).hidden) {
              this.useFixRowHeight = true;
            }
          } catch (e) {
          }
        });
      } else {
        this.useFixRowHeight = true;
      }
    }
  }

  /**
   * raise checkbox checked event for checkbox-cell
   * @param columnName
   * @param rowData
   * @param checked
   */
  onCheckFieldChecked(columnName: string, rowData: any, checked: boolean) {
    const actionData: ActionData = {
      actionname: columnName,
      rowdata: rowData,
      checked: checked
    };
    this.checkboxChecked.emit(actionData);
  }

  /**
   * get page data (LazyLoading)
   * @param lazyLoadEvent
   */
  getTablePageData(lazyLoadEvent: TableLazyLoadEvent) {
    /**
     * TODO implement default TableServiceProvider (if necessary)
     * actually parentDataProvider is required!
     * see transactions-view-component for example
     */
    if (this.parentDataProvider !== null) {
      this.dataLoading = true;
      const subscription: Subscription = this.parentDataProvider(lazyLoadEvent).subscribe({
        next: result => {
          subscription.unsubscribe();
          this.dataLoading = false;
          this.lastLazyLoadEvent = lazyLoadEvent;
          if (this.paginator === undefined) {
            this.paginator = this.tableConfig.paginator;
          }
          if (result.successful) {
            this.model = [];
            this.totalRecords = result.totalElements;
            if (this.parentDataMapper !== null) {
              result = this.parentDataMapper(result);
            }
            this.model.push(...result.payload);
            if (this.totalRecords === 0) {
              this.paginator = false;
            } else {
              this.paginator = this.tableConfig.paginator;
            }
            this.dataFetched.emit(result);
          } else {
            this.messageService.add({
              sticky: true,
              severity: 'error',
              summary: this.i18n.i18nLng()['HTTPDATAERROR'],
              detail: result.message + '\n' + result.details + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
            });
          }
        },
        error: err => {
          subscription.unsubscribe();
          // colsole.log(err);
          this.dataLoading = false;
          this.messageService.add({
            sticky: true,
            severity: 'error',
            summary: this.i18n.i18nLng()['HTTPDATAERROR'],
            detail: err.message + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
          });
        }
      });
    }
  }

  /**
   * Event - row was selected
   * @param event
   */
  onRowSelect(event) {
    this.selectionChanged.emit(event.data);
  }

  /**
   * Event - row was unselected
   * @param event
   */
  onRowUnselect(event) {
    this.selectionChanged.emit(null);
  }

  /**
   * Evemt - table state was restored
   * @param event
   * @protected
   */
  onStateRestore(event: TableState) {
    this.stateRestored.emit(event);
  }

  /**
   * reset selection when filtering
   * @param event
   * @protected
   */
  protected onFilter(event: TableFilterEvent) {
    this.selectedRowsData = null;
    this.selectionChanged.emit(null);
    if (!this.tableConfig?.lazyLoading) {
      this.totalRecords = event.filteredValue?.length || 0;
      this.totalRecordsChanged.emit(this.totalRecords);
    }
  }

  /**
   * cache last sort event
   * @param event
   * @protected
   */
  protected onSort(event: SortEvent) {
    this.lastSortEvent = event;
  }

}
